var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useMemo } from 'react';
import { Box, Typography, CircularProgress, Alert, IconButton } from '@mui/material';
import { useSelector } from 'react-redux';
import { getReportList, getReportContent, markIndividualReportAsViewed } from '../../services/sleepStudyReportService';
import ReportSideMenu from './ReportSideMenu';
import ReportHeader from './ReportHeader';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import styles from './SleepStudyResults.module.scss';
// Configure pdfjs worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
const extractDatePart = (value) => {
    if (!value)
        return undefined;
    // Preserve the original calendar day from ISO-like timestamps (avoids UTC day shift).
    const isoDateMatch = /^(\d{4}-\d{2}-\d{2})/.exec(value);
    if (isoDateMatch) {
        return isoDateMatch[1];
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return undefined;
    }
    return parsed.toISOString().split('T')[0];
};
const SleepStudyResults = ({ onReportStatusChange }) => {
    const { patientId, patientName, providerCode } = useSelector((state) => state.smart);
    const [reports, setReports] = useState([]);
    const [orderMetadata, setOrderMetadata] = useState(null);
    const [selectedReportId, setSelectedReportId] = useState();
    const [reportContent, setReportContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [contentLoading, setContentLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
    const [numPages, setNumPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    // Fetch report list on component mount
    useEffect(() => {
        if (!patientId) {
            setLoading(false);
            return;
        }
        const fetchReports = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                setLoading(true);
                setError(null);
                const data = yield getReportList(providerCode || 'virtuox', patientId);
                // Extract reports and metadata from response
                setReports(data.reports || []);
                setOrderMetadata({
                    referrerName: data.referrerName,
                    requestType: data.requestType,
                    orderedAt: data.orderedAt
                });
                // Auto-select the first report and mark it as viewed
                if (data.reports && data.reports.length > 0) {
                    const firstReportId = data.reports[0].id;
                    setSelectedReportId(firstReportId);
                    // Mark the auto-selected report as viewed
                    if (providerCode) {
                        try {
                            yield markIndividualReportAsViewed(providerCode, patientId, firstReportId);
                            // Re-fetch to get updated viewedAt timestamp
                            const updatedData = yield getReportList(providerCode, patientId);
                            setReports(updatedData.reports);
                            setOrderMetadata({
                                referrerName: updatedData.referrerName,
                                requestType: updatedData.requestType,
                                orderedAt: updatedData.orderedAt
                            });
                            // Notify parent to refresh badge
                            onReportStatusChange === null || onReportStatusChange === void 0 ? void 0 : onReportStatusChange();
                        }
                        catch (_a) {
                            // marking report as viewed failed
                        }
                    }
                }
            }
            catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to load reports';
                setError(errorMessage);
            }
            finally {
                setLoading(false);
            }
        });
        fetchReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientId, providerCode]);
    // Cleanup blob URL on unmount or when it changes
    useEffect(() => {
        return () => {
            if (pdfBlobUrl) {
                URL.revokeObjectURL(pdfBlobUrl);
            }
        };
    }, [pdfBlobUrl]);
    // Fetch selected report content
    useEffect(() => {
        if (!selectedReportId) {
            setReportContent(null);
            setPdfBlobUrl(null);
            setCurrentPage(1);
            return;
        }
        const fetchContent = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                setContentLoading(true);
                // Clean up previous blob URL
                setPdfBlobUrl((prevUrl) => {
                    if (prevUrl) {
                        URL.revokeObjectURL(prevUrl);
                    }
                    return null;
                });
                const content = yield getReportContent(providerCode || 'virtuox', selectedReportId);
                setReportContent(content);
                // If content is PDF, create Blob directly from ArrayBuffer
                if (content.content && content.contentType === 'application/pdf') {
                    const blob = new Blob([content.content], { type: 'application/pdf' });
                    const url = URL.createObjectURL(blob);
                    setPdfBlobUrl(url);
                    setCurrentPage(1);
                }
            }
            catch (err) {
                setReportContent(null);
                setPdfBlobUrl(null);
            }
            finally {
                setContentLoading(false);
            }
        });
        fetchContent();
    }, [selectedReportId, providerCode]);
    // Derive study date and interpreter from the currently selected report
    const selectedReport = useMemo(() => reports.find((r) => r.id === selectedReportId), [reports, selectedReportId]);
    const selectedStudyDate = useMemo(() => {
        if (selectedReport === null || selectedReport === void 0 ? void 0 : selectedReport.studyPeriodStart) {
            return extractDatePart(selectedReport.studyPeriodStart) || '';
        }
        return (reportContent === null || reportContent === void 0 ? void 0 : reportContent.studyDate) || '';
    }, [selectedReport, reportContent]);
    const selectedInterpreter = useMemo(() => (selectedReport === null || selectedReport === void 0 ? void 0 : selectedReport.interpreterName) || (reportContent === null || reportContent === void 0 ? void 0 : reportContent.interpreter) || '', [selectedReport, reportContent]);
    // Most recent updatedAt: take the latest createdAt among all current-status reports
    const mostRecentUpdatedDate = useMemo(() => {
        const currentReports = reports.filter((r) => r.status === 'current');
        if (currentReports.length === 0)
            return undefined;
        const sorted = [...currentReports].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        return sorted[0].createdAt;
    }, [reports]);
    const reportGroups = useMemo(() => {
        if (reports.length === 0)
            return [];
        const currentReports = reports.filter((r) => r.status === 'current');
        const supersededReports = reports.filter((r) => r.status === 'superseded');
        const statusGroups = [];
        if (currentReports.length > 0) {
            statusGroups.push({ status: 'current', label: 'CURRENT', reports: currentReports });
        }
        if (supersededReports.length > 0) {
            statusGroups.push({ status: 'superseded', label: 'DEPRECATED', reports: supersededReports });
        }
        // Format order date from metadata
        let orderDate = 'Sleep Study Order';
        if (orderMetadata) {
            const formattedDate = extractDatePart(orderMetadata.orderedAt);
            orderDate = formattedDate ? `${formattedDate} ${orderMetadata.requestType} Order` : `${orderMetadata.requestType} Order`;
        }
        return [
            {
                orderId: 'order-1',
                orderDate,
                studyType: (orderMetadata === null || orderMetadata === void 0 ? void 0 : orderMetadata.requestType) || 'HST',
                statusGroups
            }
        ];
    }, [reports, orderMetadata]);
    const handleReportSelect = (reportId) => __awaiter(void 0, void 0, void 0, function* () {
        setSelectedReportId(reportId);
        // Mark report as viewed in backend
        if (patientId && providerCode) {
            try {
                yield markIndividualReportAsViewed(providerCode, patientId, reportId);
                // Re-fetch reports to update viewedAt timestamps
                const updatedData = yield getReportList(providerCode, patientId);
                setReports(updatedData.reports);
                // Notify parent to refresh report status (for badge update)
                onReportStatusChange === null || onReportStatusChange === void 0 ? void 0 : onReportStatusChange();
            }
            catch (_a) {
                // Don't block user interaction if marking viewed fails
            }
        }
    });
    const onDocumentLoadSuccess = ({ numPages: loadedNumPages }) => {
        setNumPages(loadedNumPages);
    };
    const goToPreviousPage = () => {
        setCurrentPage((prev) => Math.max(1, prev - 1));
    };
    const goToNextPage = () => {
        setCurrentPage((prev) => Math.min(numPages, prev + 1));
    };
    if (loading) {
        return (_jsx(Box, { className: styles.loading_container, children: _jsxs(Box, { sx: { textAlign: 'center' }, children: [_jsx(CircularProgress, {}), _jsxs(Typography, { variant: "body2", sx: { mt: 2 }, color: "text.secondary", children: ["Loading reports for patient ", patientName, "..."] })] }) }));
    }
    if (error) {
        return (_jsx(Box, { className: styles.error_container, children: _jsxs(Alert, { severity: "error", children: [_jsx(Typography, { variant: "body2", fontWeight: "bold", children: "Error loading reports" }), _jsx(Typography, { variant: "body2", children: error })] }) }));
    }
    if (reports.length === 0 && !loading) {
        return (_jsx(Box, { className: styles.empty_container, children: _jsxs(Alert, { severity: "info", sx: { maxWidth: 600 }, children: [_jsx(Typography, { variant: "body1", fontWeight: "bold", gutterBottom: true, children: "No reports available" }), _jsx(Typography, { variant: "body2", children: "No sleep study reports are available for this patient yet." })] }) }));
    }
    return (_jsxs(Box, { className: styles.results_container, sx: { pt: 0, mt: '-24px' }, children: [_jsx(Box, { className: styles.content_area, children: contentLoading ? (_jsx(Box, { className: styles.loading_container, children: _jsx(CircularProgress, {}) })) : reportContent ? (_jsxs(_Fragment, { children: [_jsx(ReportHeader, { studyDate: selectedStudyDate, testType: (orderMetadata === null || orderMetadata === void 0 ? void 0 : orderMetadata.requestType) || reportContent.testType || 'HST', interpreter: selectedInterpreter, referrer: reportContent.referrer || (orderMetadata === null || orderMetadata === void 0 ? void 0 : orderMetadata.referrerName) || '', updatedDate: mostRecentUpdatedDate }), _jsx(Box, { className: styles.report_content_wrapper, children: pdfBlobUrl ? (_jsxs(Box, { className: styles.pdf_viewer, children: [_jsx(Document, { file: pdfBlobUrl, onLoadSuccess: onDocumentLoadSuccess, loading: _jsx(Box, { display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400, children: _jsx(CircularProgress, {}) }), error: _jsx(Box, { display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400, children: _jsx(Alert, { severity: "error", children: "Failed to load PDF document" }) }), children: _jsx(Page, { pageNumber: currentPage, renderTextLayer: true, renderAnnotationLayer: true, width: Math.min(window.innerWidth * 0.5, 800) }) }), numPages > 1 && (_jsxs(Box, { className: styles.pagination_controls, children: [_jsx(IconButton, { onClick: goToPreviousPage, disabled: currentPage <= 1, size: "small", children: _jsx(NavigateBeforeIcon, {}) }), _jsxs(Typography, { variant: "body2", children: ["Page ", currentPage, " of ", numPages] }), _jsx(IconButton, { onClick: goToNextPage, disabled: currentPage >= numPages, size: "small", children: _jsx(NavigateNextIcon, {}) })] }))] })) : reportContent.contentType === 'text/html' && typeof reportContent.content === 'string' ? (_jsx(Box, { dangerouslySetInnerHTML: { __html: reportContent.content } })) : (_jsxs(Box, { children: [_jsxs(Typography, { variant: "body2", color: "text.secondary", children: ["Content type: ", reportContent.contentType || 'unknown'] }), typeof reportContent.content === 'string' && (_jsxs(Typography, { variant: "caption", display: "block", sx: { mt: 1 }, children: ["Content preview: ", reportContent.content.substring(0, 100)] }))] })) })] })) : (_jsx(Box, { className: styles.empty_container, children: _jsx(Typography, { variant: "body1", color: "text.secondary", children: "Select a report to view its content." }) })) }), _jsx(Box, { className: styles.side_menu_wrapper, children: _jsx(ReportSideMenu, { reportGroups: reportGroups, selectedReportId: selectedReportId, onReportSelect: handleReportSelect }) })] }));
};
export default SleepStudyResults;
